import {useEffect} from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import {useShopping} from '../contexts/ShoppingContext';
import {useClientDataContext} from '../contexts/ClientDataContext';

export function CategoryNav() {
    const {state, departmentsLoading} = useShopping();
    const navigate = useNavigate();
    const location = useLocation();
    const {clientData, loading: clientLoading} = useClientDataContext();
    const themeColors = clientData?.themeColors;

    const urlParams = new URLSearchParams(location.search);
    const selectedDepartmentId = urlParams.get('departmentId') ? Number(urlParams.get('departmentId')) : null;
    const selectedCategoryId = urlParams.get('categoryId') ? Number(urlParams.get('categoryId')) : null;

    const departments = state.departments;
    const selectedDepartment = departments?.find(d => d.id === selectedDepartmentId);
    const categories = selectedDepartment?.productCategories || [];
    const isSingleCategory = categories.length === 1;

    // Auto-select the only category if none selected yet — must be before any early return
    useEffect(() => {
        if (isSingleCategory && !selectedCategoryId && selectedDepartmentId) {
            navigate(`/?departmentId=${selectedDepartmentId}&categoryId=${categories[0].id}`);
        }
    }, [isSingleCategory, selectedCategoryId, selectedDepartmentId]); // eslint-disable-line react-hooks/exhaustive-deps

    // Show skeleton while departments are loading but we know one is selected (URL has departmentId)
    if (selectedDepartmentId && departmentsLoading) {
        return (
            <aside
                className="hidden md:flex md:flex-col w-56 border-r border-white/10 fixed left-0 bottom-0 z-30"
                style={{ top: 'var(--header-height, 108px)', backgroundColor: themeColors?.left_side_background || '#1f2937' }}
                aria-busy="true"
                aria-label="Loading navigation"
            >
                <div className="py-5 px-3 space-y-2 animate-pulse">
                    <div className="h-4 bg-white/10 rounded w-2/3 mx-3 mb-4" />
                    <div className="h-7 bg-white/10 rounded mx-1" />
                    {[70, 85, 60, 90, 75].map((w, i) => (
                        <div key={i} className="h-7 bg-white/10 rounded mx-1" style={{ width: `${w}%` }} />
                    ))}
                </div>
            </aside>
        );
    }

    // Hide if no department selected or it has no subcategories
    if (!selectedDepartmentId || !selectedDepartment || categories.length === 0) {
        return null;
    }

    const activeColor = themeColors?.left_side_active_color || '#ffffff';
    const itemColor = themeColors?.left_side_color || '#d1d5db';
    const headingColor = themeColors?.left_side_heading_color || themeColors?.left_side_color || '#9ca3af';

    const handleAllInDepartmentClick = () => {
        if (!selectedCategoryId) return;
        navigate(`/?departmentId=${selectedDepartmentId}`);
    };

    const handleCategoryClick = (categoryId: number) => {
        if (selectedCategoryId === categoryId) return;
        navigate(`/?departmentId=${selectedDepartmentId}&categoryId=${categoryId}`);
    };

    const isAllActive = !selectedCategoryId;
    const isActiveCategory = (categoryId: number) => selectedCategoryId === categoryId;

    if (clientLoading || !themeColors) return (
        <aside
            className="hidden md:flex md:flex-col w-56 border-r border-white/10 fixed left-0 bottom-0 z-30 bg-gray-800"
            style={{ top: 'var(--header-height, 108px)' }}
            aria-busy="true"
            aria-label="Loading navigation"
        >
            <div className="py-5 px-3 space-y-2 animate-pulse">
                <div className="h-4 bg-white/10 rounded w-2/3 mx-3 mb-4" />
                <div className="h-7 bg-white/10 rounded mx-1" />
                {[70, 85, 60, 90, 75].map((w, i) => (
                    <div key={i} className="h-7 bg-white/10 rounded mx-1" style={{ width: `${w}%` }} />
                ))}
            </div>
        </aside>
    );

    return (
        <aside
            className="hidden md:flex md:flex-col w-56 border-r border-white/10 overflow-y-auto overscroll-none fixed left-0 bottom-0 z-30 transition-colors duration-500"
            style={{
                top: 'var(--header-height, 108px)',
                backgroundColor: themeColors?.left_side_background
            }}
            aria-label="Category navigation"
        >
            <nav className="py-4">
                {/* Header */}
                <p
                    className="px-4 mb-2 text-xs font-semibold uppercase tracking-widest opacity-60"
                    style={{ color: headingColor }}
                >
                    Categories
                </p>

                <div className="px-2 space-y-1">
                    {/* All in department — hidden when only one category (auto-selected) */}
                    {!isSingleCategory && (
                        <div className="pb-1 mb-1" style={{ borderBottom: `1px solid ${itemColor}20` }}>
                            <button
                                onClick={handleAllInDepartmentClick}
                                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-150 outline-none focus:outline-none focus-visible:outline-none ${!isAllActive ? 'active:brightness-[0.6]' : ''}`}
                                style={{
                                    color: isAllActive ? activeColor : itemColor,
                                    backgroundColor: isAllActive ? `color-mix(in srgb, ${activeColor} 35%, transparent)` : undefined,
                                    fontWeight: isAllActive ? 700 : 500,
                                    opacity: isAllActive ? 1 : 0.9,
                                }}
                                onMouseEnter={e => { if (!isAllActive) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)'; }}
                                onMouseLeave={e => { if (!isAllActive) e.currentTarget.style.backgroundColor = ''; }}
                                aria-current={isAllActive ? 'page' : undefined}
                            >
                                All categories
                            </button>
                        </div>
                    )}

                    {/* Subcategories */}
                    {categories.map(category => {
                        const active = isActiveCategory(category.id);
                        return (
                            <button
                                key={category.id}
                                onClick={() => handleCategoryClick(category.id)}
                                className={`w-full text-left px-3 py-2 text-sm rounded-lg transition-all duration-150 outline-none focus:outline-none focus-visible:outline-none ${!active ? 'active:brightness-[0.6]' : ''}`}
                                style={{
                                    color: active ? activeColor : itemColor,
                                    backgroundColor: active ? `color-mix(in srgb, ${activeColor} 35%, transparent)` : undefined,
                                    fontWeight: active ? 600 : 400,
                                }}
                                onMouseEnter={e => { if (!active) e.currentTarget.style.backgroundColor = 'rgba(255,255,255,0.14)'; }}
                                onMouseLeave={e => { if (!active) e.currentTarget.style.backgroundColor = ''; }}
                                aria-current={active ? 'page' : undefined}
                            >
                                {category.name}{category.memo ? ` (${category.memo})` : ''}
                            </button>
                        );
                    })}
                </div>

                {/* Navigation Links for this department */}
                {selectedDepartment.navigationLinks && selectedDepartment.navigationLinks.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-white/10">
                        {selectedDepartment.navigationLinks.map((link, index) => (
                            <a
                                key={index}
                                href={link.url.startsWith('http') ? link.url : `https://${link.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="w-full text-left px-5 py-2 text-xs transition-all duration-150 focus:outline-none flex items-center gap-2 opacity-70 hover:opacity-100"
                                style={{ color: itemColor }}
                            >
                                <svg className="w-3 h-3 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                </svg>
                                {link.label}
                            </a>
                        ))}
                    </div>
                )}
            </nav>
        </aside>
    );
}
